import { Link } from 'react-router-dom';
import type { WasBudgetOverzicht } from '@fleet/shared';
import { euro } from '@fleet/shared';
import { Eyebrow } from '../../components/ui/Eyebrow';
import { InfoTip } from '../../components/ui/InfoTip';
import { MeerTonen } from '../../components/ui/MeerTonen';
import { Panel } from '../../components/ui/Panel';
import { PersoonLink } from '../../components/ui/EntiteitLink';

/** De wassen-kleur uit BUCKETS, zodat dit blok bij het kostenoverzicht hoort. */
const WASSEN = '#6E9AA5';
const OVER = '#B0543B';
const KOERS = '#E0A943';

/**
 * Who is over their washing allowance.
 *
 * Only the rows that ask something of you are on screen: over budget, or on
 * track to get there before December. The other fourteen are doing nothing
 * wrong, and listing them turned a two-person question into a twenty-one-row
 * table. They are one click away.
 *
 * Status is carried by colour rather than a label per row — terracotta past the
 * budget line, amber for on-track — because the same four words repeated down a
 * column is what made this panel long in the first place.
 */
export function WasBudgetPanel({ was }: { was: WasBudgetOverzicht }) {
  const uit = was.budgetPerJaar == null;

  // Eerst wie er iets van je vraagt, daarna de rest — en de knop knipt precies
  // daartussen. Op volgorde zetten in plaats van op aantal, want 'op koers' hangt
  // aan een prognose en niet aan een plek in de ranglijst.
  const opvallend = was.regels.filter((r) => r.status === 'over' || r.status === 'koers');
  const rustig = was.regels.filter((r) => r.status !== 'over' && r.status !== 'koers');
  const gesorteerd = uit ? was.regels : [...opvallend, ...rustig];

  // Doorlopen tot de grootste overschrijding, met de budgetgrens als streep: zo
  // lees je hoe ver iemand eroverheen zit en niet alleen dát hij eroverheen zit.
  const maxDeel = Math.max(1, ...was.regels.map((r) => r.deel ?? 0));
  const budgetOp = (1 / maxDeel) * 100;

  const toelichting = [
    'Alles wat op de tankpas als wasbeurt is geboekt, per persoon per kalenderjaar en excl. btw.',
    'Het budget telt vanaf de maand dat iemand een pas krijgt, dus wie later instroomt heeft naar rato minder.',
    was.zonderWasbeurt > 0
      ? was.zonderWasbeurt === 1
        ? '1 collega met een pas heeft dit jaar niet gewassen en staat niet in de lijst.'
        : `${was.zonderWasbeurt} collega's met een pas hebben dit jaar niet gewassen en staan niet in de lijst.`
      : '',
    was.ongekoppeld > 0
      ? `${euro(was.ongekoppeld)} hangt aan geen persoon en telt alleen mee in het totaal.`
      : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Panel>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 16,
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Eyebrow>Wasbudget {was.jaar}</Eyebrow>
            <InfoTip uitlijning="links" tekst={toelichting} />
          </div>
          <div style={{ fontSize: 26, fontWeight: 900, color: '#2A6085', marginTop: 4 }}>
            {euro(was.totaalBesteed)}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          {uit ? (
            <div style={{ fontSize: 13, color: '#666666' }}>
              Geen budget ingesteld ·{' '}
              <Link to="/instellingen" style={{ color: '#2A6085', fontWeight: 700 }}>
                stel er een in
              </Link>
            </div>
          ) : (
            <>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: was.overschrijders.length ? 700 : 400,
                  color: was.overschrijders.length ? OVER : '#666666',
                }}
              >
                {was.overschrijders.length} van {was.regels.length} over budget
              </div>
              <div style={{ fontSize: 11.5, color: '#999999', marginTop: 2 }}>
                {euro(was.budgetPerJaar!)} p.p. per jaar
              </div>
            </>
          )}
        </div>
      </div>

      {/* Niemand die opvalt is goed nieuws en hoort er te staan; de lijst blijft
          er wel onder, achter de knop. Anders krijg je een leeg paneel met alleen
          een uitklapper eronder. */}
      {!uit && opvallend.length === 0 && (
        <div style={{ fontSize: 13, color: '#2A7050', fontWeight: 700, marginBottom: 4 }}>
          Niemand zit over het budget of gaat er op dit tempo overheen.
        </div>
      )}

      {gesorteerd.length === 0 ? (
        <div style={{ fontSize: 13, color: '#888888' }}>Geen wasbeurten geboekt in {was.jaar}.</div>
      ) : (
        <MeerTonen
          items={gesorteerd}
          cap={uit ? gesorteerd.length : opvallend.length}
          meer={(n) => `+${n} binnen budget tonen`}
        >
          {(zicht) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {zicht.map((r) => {
                const deel = r.deel ?? 0;
                const binnen = (Math.min(deel, 1) / maxDeel) * 100;
                const eroverheen = deel > 1 ? ((deel - 1) / maxDeel) * 100 : 0;
                const kleur = r.status === 'koers' ? KOERS : WASSEN;
                return (
                  <div
                    key={r.persoonId ?? r.naam}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '132px minmax(0, 1fr) 128px',
                      gap: 14,
                      alignItems: 'center',
                    }}
                  >
                    <PersoonLink
                      persoonId={r.persoonId}
                      naam={r.naam}
                      titel={`${r.wasbeurten} ${r.wasbeurten === 1 ? 'wasbeurt' : 'wasbeurten'}, gemiddeld ${
                        r.gemiddeldePerBeurt ? euro(r.gemiddeldePerBeurt) : '—'
                      }${r.actieveMaanden < 12 ? ` · ${r.actieveMaanden} maanden een pas dit jaar` : ''}`}
                      style={{
                        fontSize: 13,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    />

                    <div
                      style={{
                        position: 'relative',
                        background: '#F0E8DC',
                        height: 20,
                        borderRadius: 3,
                        overflow: 'hidden',
                        display: 'flex',
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          background: kleur,
                          width: `${uit ? (deel ? binnen : 100) : binnen}%`,
                          animation: 'vsGrowW .6s cubic-bezier(.4,0,.2,1) both',
                        }}
                      />
                      {eroverheen > 0 && (
                        <div
                          style={{
                            height: '100%',
                            background: OVER,
                            width: `${eroverheen}%`,
                            animation: 'vsGrowW .6s cubic-bezier(.4,0,.2,1) both',
                          }}
                        />
                      )}
                      {!uit && maxDeel > 1 && (
                        <span
                          title={`budget ${euro(r.budget ?? 0)}`}
                          style={{
                            position: 'absolute',
                            left: `${budgetOp}%`,
                            top: 0,
                            bottom: 0,
                            width: 1,
                            background: '#111111',
                            opacity: 0.55,
                          }}
                        />
                      )}
                    </div>

                    {/* Alleen het besteed bedrag. Het budget naar rato stond er
                        eerder bij wie later instroomde, maar dat is één regel
                        uitleg per persoon in een kolom die verder overal hetzelfde
                        getal zou tonen; dat het naar rato gaat staat nu één keer
                        achter de `i`, en het bedrag zelf in de tooltip op de naam. */}
                    <span
                      style={{
                        fontSize: 12.5,
                        textAlign: 'right',
                        color: '#444444',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <strong style={{ color: '#111111' }}>{euro(r.besteed)}</strong>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </MeerTonen>
      )}
    </Panel>
  );
}
